import React, { useEffect, useState } from 'react';
import { Row, Col, Table, Badge, Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { useTheme } from '../../../context/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  adminFetchPlans,
  adminCreatePlan,
  adminUpdatePlan,
  adminDeletePlan,
} from '../../../features/subscriptions/subscriptionsSlice';
import { SubscriptionPlan, SubscriptionPlanCreate, SubscriptionPlanUpdate } from '../../../features/subscriptions/subscriptionsService';

const EMPTY_FORM: SubscriptionPlanCreate = {
  name: '',
  description: '',
  price: 0,
  currency: 'usd',
  interval: 'month',
  interval_count: 1,
  trial_days: 0,
  features: [],
  max_chatbots: undefined,
  max_messages_per_month: undefined,
  max_knowledge_sources: undefined,
  is_active: true,
  sort_order: 0,
};

const PlansManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const dispatch = useAppDispatch();
  const { adminPlans, isLoading, error } = useAppSelector((state) => state.subscriptions);

  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [form, setForm] = useState<SubscriptionPlanCreate>(EMPTY_FORM);
  const [featuresText, setFeaturesText] = useState('');

  useEffect(() => {
    dispatch(adminFetchPlans());
  }, [dispatch]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(EMPTY_FORM);
    setFeaturesText('');
    setShowModal(true);
  };

  const openEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      description: plan.description || '',
      price: plan.price,
      currency: plan.currency,
      interval: plan.interval,
      interval_count: plan.interval_count,
      trial_days: plan.trial_days,
      features: plan.features || [],
      max_chatbots: plan.max_chatbots ?? undefined,
      max_messages_per_month: plan.max_messages_per_month ?? undefined,
      max_knowledge_sources: plan.max_knowledge_sources ?? undefined,
      is_active: plan.is_active,
      sort_order: plan.sort_order,
    });
    setFeaturesText((plan.features || []).join('\n'));
    setShowModal(true);
  };

  const handleSubmit = async () => {
    const features = featuresText
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean);

    const payload = { ...form, features };

    if (editingPlan) {
      await dispatch(adminUpdatePlan({ id: editingPlan.id, data: payload as SubscriptionPlanUpdate }));
    } else {
      await dispatch(adminCreatePlan(payload));
    }
    setShowModal(false);
    dispatch(adminFetchPlans());
  };

  const handleDeactivate = async (planId: number) => {
    if (window.confirm(t('confirm_deactivate_plan', 'Are you sure you want to deactivate this plan?'))) {
      await dispatch(adminDeletePlan(planId));
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(price);
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: 20,
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
  };

  return (
    <div className="animate-fade-in" style={{ padding: '1.5rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold mb-0" style={{ color: 'var(--text-primary)' }}>
          {t('plans_management', 'Plans Management')}
        </h3>
        <Button variant="primary" size="sm" className="btn-modern btn-primary" onClick={openCreate} style={{ width: '150px', borderRadius: 10, padding: '0.4rem 1rem', fontSize: '0.82rem' }}>
          <FaPlus className="me-1" /> {t('create_plan', 'Create Plan')}
        </Button>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <div style={cardStyle}>
        {isLoading && adminPlans.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: 'var(--brand-primary)' }} />
          </div>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                {[t('name', 'Name'), t('price', 'Price'), t('interval', 'Interval'), t('trial', 'Trial'), t('limits', 'Limits'), t('status', 'Status'), t('order', 'Order'), t('actions', 'Actions')].map((label) => (
                  <th key={label} style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.02em' }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adminPlans.map((plan) => (
                <tr key={plan.id}>
                  <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)', fontWeight: 600 }}>{plan.name}</td>
                  <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>{formatPrice(plan.price, plan.currency)}</td>
                  <td style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}>
                    {plan.interval_count > 1 ? `${plan.interval_count} ` : ''}{plan.interval}
                  </td>
                  <td style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}>{plan.trial_days > 0 ? `${plan.trial_days}d` : '—'}</td>
                  <td style={{ borderColor: 'var(--border-color)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {plan.max_chatbots != null && <div>Chatbots: {plan.max_chatbots}</div>}
                    {plan.max_messages_per_month != null && <div>Msgs: {plan.max_messages_per_month.toLocaleString()}</div>}
                    {plan.max_knowledge_sources != null && <div>KB: {plan.max_knowledge_sources}</div>}
                  </td>
                  <td style={{ borderColor: 'var(--border-color)' }}>
                    <Badge bg={plan.is_active ? 'success' : 'secondary'}>
                      {plan.is_active ? <FaCheck /> : <FaTimes />} {plan.is_active ? t('active', 'Active') : t('inactive', 'Inactive')}
                    </Badge>
                  </td>
                  <td style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}>{plan.sort_order}</td>
                  <td style={{ borderColor: 'var(--border-color)' }}>
                    <div className="action-buttons-modern">
                      <button className="action-btn-modern" title={t('edit', 'Edit')} onClick={() => openEdit(plan)}>
                        <FaEdit />
                      </button>
                      {plan.is_active && (
                        <button className="action-btn-modern text-danger" title={t('deactivate', 'Deactivate')} onClick={() => handleDeactivate(plan.id)}>
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {adminPlans.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-4" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
                    {t('no_plans', 'No plans found')}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="modern-modal">
        <Modal.Header closeButton className="modern-modal-header">
          <Modal.Title>
            {editingPlan ? t('edit_plan', 'Edit Plan') : t('create_plan', 'Create Plan')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('name', 'Name')}</Form.Label>
                <Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Pro" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('price', 'Price')}</Form.Label>
                <Form.Control type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('currency', 'Currency')}</Form.Label>
                <Form.Select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  <option value="usd">USD</option>
                  <option value="eur">EUR</option>
                  <option value="gbp">GBP</option>
                  <option value="try">TRY</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('description', 'Description')}</Form.Label>
                <Form.Control as="textarea" rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('interval', 'Interval')}</Form.Label>
                <Form.Select value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })}>
                  <option value="month">{t('monthly', 'Monthly')}</option>
                  <option value="year">{t('yearly', 'Yearly')}</option>
                  <option value="week">{t('weekly', 'Weekly')}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('interval_count', 'Interval Count')}</Form.Label>
                <Form.Control type="number" min="1" value={form.interval_count} onChange={(e) => setForm({ ...form, interval_count: parseInt(e.target.value) || 1 })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('trial_days', 'Trial Days')}</Form.Label>
                <Form.Control type="number" min="0" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value) || 0 })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('max_chatbots', 'Max Chatbots')}</Form.Label>
                <Form.Control type="number" min="0" value={form.max_chatbots ?? ''} onChange={(e) => setForm({ ...form, max_chatbots: e.target.value ? parseInt(e.target.value) : undefined })} placeholder={t('unlimited', 'Unlimited')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('max_messages', 'Max Messages/Month')}</Form.Label>
                <Form.Control type="number" min="0" value={form.max_messages_per_month ?? ''} onChange={(e) => setForm({ ...form, max_messages_per_month: e.target.value ? parseInt(e.target.value) : undefined })} placeholder={t('unlimited', 'Unlimited')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('max_knowledge_sources', 'Max Knowledge Sources')}</Form.Label>
                <Form.Control type="number" min="0" value={form.max_knowledge_sources ?? ''} onChange={(e) => setForm({ ...form, max_knowledge_sources: e.target.value ? parseInt(e.target.value) : undefined })} placeholder={t('unlimited', 'Unlimited')} />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>{t('features', 'Features')} ({t('one_per_line', 'one per line')})</Form.Label>
                <Form.Control as="textarea" rows={4} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder={"AI Chatbot Support\nEmail Integration\nPriority Support"} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t('sort_order', 'Sort Order')}</Form.Label>
                <Form.Control type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Form.Check type="switch" label={t('active', 'Active')} checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer border-0 pt-0 pb-4 justify-content-end px-4">
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button variant="outline-secondary" className="btn-minimal" onClick={() => setShowModal(false)} disabled={isLoading}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button variant="info" className="btn-minimal" onClick={handleSubmit} disabled={!form.name || isLoading}>
              {isLoading ? (
                <><Spinner animation="border" size="sm" className="me-1" /> {t('saving', 'Saving...')}</>
              ) : (
                editingPlan ? t('save', 'Save') : t('create', 'Create')
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlansManagementPage;
